import { api } from '@mbfab/client-api'
import { isSome } from '@mbfab/shared'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import type { MillsheetBulkEditFields } from '@/components/screens/millsheet/bulk-edit-dialog'
import type { MillsheetFilterOptions } from '@/components/screens/millsheet/filter-state'
import {
  EMPTY_MILLSHEET_SEARCH,
  parseMillsheetSearch,
  toMillsheetRows,
  toMillsheetSortInputs,
} from '@/components/screens/millsheet/filter-state'
import { MillsheetWorkspace } from '@/components/screens/millsheet/millsheet-workspace'
import type { MillsheetBulkTarget, MillsheetSearch } from '@/components/screens/millsheet/millsheets-table'
import { toS3ObjectsForPrint } from '@/components/screens/millsheet/print/print-types'
import type { Feedback } from '@/components/screens/millsheet/types'
import type { DataTableSorter } from '@/components/ui/data-table/data-table'
import { useFilterBarState } from '@/components/ui/filter-bar'
import { useFilterScopedPage } from '@/components/ui/use-filter-scoped-page'
import { runMutation as runMutationWithFeedback } from '@/lib/run-mutation'
import { useMe, useMyIdentity } from '@/providers/me-context'

// フィルタ候補未取得（初回ロード中）の既定値。ページヘッダー・フィルタバーを常にマウントしたまま
// テーブルだけをスケルトンにするため（画面まるごとの CenteredLoading 差し替えをやめる。2026/08 FB対応）。
// order-matching-container.tsx（未紐付けミルシート一覧タブ）でも同じ形の既定値が必要なため、
// ここを正としてエクスポートする（2026/08 レビューFBで重複が発覚。以前は同一リテラルが2箇所にあった）
export const EMPTY_MILLSHEET_FILTER_OPTIONS: MillsheetFilterOptions = {
  manufacturers: [],
  tradingCompanies: [],
  customers: [],
  constructionNames: [],
  constructionAreas: [],
  members: [],
  buildingParts: [],
  steelTypes: [],
  steelSectionTypes: [],
  submissionDates: [],
  endorsementCompanies: [],
}

// ミルシート一覧（ホーム）のコンテナ。GraphQL のクエリ・ミューテーション配線を担い、
// 画面コンポーネント（MillsheetWorkspace）へ props として渡す。
// route（routes/t/$tenant_id/index.tsx）から切り出しているのは、storybook の
// 擬似e2e story が route ファイルを経由せずにコンテナごとマウントできるようにするため
export function MillsheetHomeContainer({ tenantId }: { tenantId: string }) {
  const me = useMe()
  const myIdentity = useMyIdentity()
  const client = api.useClient()
  const navigate = useNavigate()
  // 詳細・編集画面から戻る・リロード・URL 共有で条件を保つため URL に同期する（初回の取得から条件つきにする）
  const [search, setSearch] = useFilterBarState<MillsheetSearch>(EMPTY_MILLSHEET_SEARCH, {
    syncUrl: true,
    urlKey: 'millsheetsFilter',
    parse: parseMillsheetSearch,
  })
  // 条件が URL 経由（ブラウザの戻る/進む）で変わったときも 1 ページ目へ戻す
  const [page, setPage] = useFilterScopedPage(search)
  const [sorters, setSorters] = useState<DataTableSorter[]>([])
  const input = useMemo(
    () => ({ ...search, sorts: toMillsheetSortInputs(sorters), page, pageSize: 100 }),
    [search, sorters, page],
  )
  const millsheetsQuery = api.useFetchPagedMillsheetsQuery({ variables: { input } })
  const filterOptionsQuery = api.useFetchMillsheetsFilterOptionsQuery()
  // バッジは補助表示なので、取得に失敗しても一覧は止めない（error は見ない）
  const duplicateCountQuery = api.useFetchMillsheetDuplicateGroupCountQuery({
    variables: { unconfirmedOnly: true },
  })
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const millsheetsResult = millsheetsQuery.data?.pagedMillsheets ?? millsheetsQuery.previousData?.pagedMillsheets

  if (isSome(millsheetsQuery.error) || isSome(filterOptionsQuery.error)) {
    return (
      <GraphqlErrorPanel
        layout="section"
        error={millsheetsQuery.error ?? filterOptionsQuery.error}
        subject="ミルシート一覧"
        onRetry={() => {
          void millsheetsQuery.refetch()
          void filterOptionsQuery.refetch()
        }}
      />
    )
  }

  const refetchMillsheetData = async (): Promise<void> => {
    await Promise.all([millsheetsQuery.refetch(), filterOptionsQuery.refetch(), duplicateCountQuery.refetch()])
  }

  // mutation の実行 + トースト + ResultBanner 用 state 更新を共通ヘルパーに委譲する
  // （docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」）。
  // 呼び出し側の書き味を変えないよう、既存の (mutate, successMessage, failureMessage) の形は維持する
  const runMutation = async (
    mutate: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ): Promise<boolean> =>
    await runMutationWithFeedback({
      mutate,
      success: { message: successMessage },
      failure: { message: failureMessage },
      refetch: refetchMillsheetData,
      onResult: setFeedback,
    })

  return (
    <MillsheetWorkspace
      search={search}
      millsheets={millsheetsResult?.millsheets ?? []}
      loading={millsheetsQuery.loading}
      orderMatchingEnabled={me.tenant.setting?.orderMatchingEnabled === true}
      millsheetSortConfig={me.tenant.setting?.millsheetSortConfig ?? {}}
      feedback={feedback}
      onDismissFeedback={() => {
        setFeedback(null)
      }}
      showPdfUpload={myIdentity.role === 'admin'}
      showCsvImport={myIdentity.role === 'admin'}
      onNavigateToUpload={() => {
        void navigate({ to: '/t/$tenant_id/upload', params: { tenant_id: tenantId }, search: { from: 'millsheets' } })
      }}
      onNavigateToCsvImport={() => {
        void navigate({ to: '/t/$tenant_id/millsheets/csv_import', params: { tenant_id: tenantId } })
      }}
      unconfirmedDuplicateGroupCount={duplicateCountQuery.data?.millsheetDuplicateGroupCount ?? 0}
      onNavigateToDuplicates={() => {
        // 重複確認画面で承認・削除すると件数が変わる。往復の前提で、遷移前に取り直しておく
        // （戻ったときにキャッシュの古い件数が一瞬見えないようにするため）
        void duplicateCountQuery.refetch().finally(() => {
          void navigate({ to: '/t/$tenant_id/millsheets/duplicates', params: { tenant_id: tenantId } })
        })
      }}
      onUpdateMillsheet={async (id, fields) =>
        await runMutation(
          async () => await client.updateMillsheet({ input: { id, ...fields } }),
          '更新しました',
          '更新に失敗しました',
        )
      }
      onUpdateMillsheetItem={async (id, fields) =>
        await runMutation(
          async () => await client.updateMillsheetItem({ input: { id, ...fields } }),
          '更新しました',
          '更新に失敗しました',
        )
      }
      onBulkEdit={async (target, fields: MillsheetBulkEditFields, count) =>
        await runMutation(
          async () => await client.updateMillsheets({ input: { target, ...fields } }),
          `${count}件を更新しました`,
          '一括編集に失敗しました',
        )
      }
      onDelete={async (id) =>
        await runMutation(async () => await client.deleteMillsheet({ id }), '削除しました', '削除に失敗しました')
      }
      onDownloadS3Object={async (s3ObjectId) => {
        const result = await client.fetchPagedS3Objects({ ids: [s3ObjectId] })
        const found = result.pagedS3Objects.s3Objects[0]
        return isSome(found) ? found.fileUrl : null
      }}
      onFetchPrintImages={async (s3ObjectIds) => {
        try {
          if (s3ObjectIds.length === 0) {
            return []
          }
          const result = await client.fetchPagedS3Objects({ ids: s3ObjectIds })
          return toS3ObjectsForPrint(result.pagedS3Objects.s3Objects)
        } catch {
          return null
        }
      }}
      filterOptions={filterOptionsQuery.data?.millsheetsFilterOptions ?? EMPTY_MILLSHEET_FILTER_OPTIONS}
      pagination={{ page, pageSize: 100, totalCount: millsheetsResult?.totalCount ?? 0 }}
      onChangePage={setPage}
      onChangeSearch={(nextSearch, nextSorters) => {
        setSearch(nextSearch)
        setSorters(nextSorters)
        setPage(1)
      }}
      onCountAllResults={async (targetSearch) => {
        try {
          const result = await client.fetchPagedMillsheets({
            input: { ...targetSearch, page: 1, pageSize: 1 },
          })
          return result.pagedMillsheets.totalCount
        } catch {
          return null
        }
      }}
      onFetchResultsForPrint={async (target: MillsheetBulkTarget, targetSorters, targetCount) => {
        try {
          const baseInput = 'ids' in target ? { filter: { ids: target.ids }, freeWord: '' } : target.filter
          const sorts = toMillsheetSortInputs(targetSorters)
          const first = await client.fetchPagedMillsheets({ input: { ...baseInput, sorts, page: 1, pageSize: 200 } })
          const second =
            targetCount > 200
              ? await client.fetchPagedMillsheets({ input: { ...baseInput, sorts, page: 2, pageSize: 200 } })
              : null
          const rows = [...first.pagedMillsheets.millsheets, ...(second?.pagedMillsheets.millsheets ?? [])]
          return toMillsheetRows(rows)
        } catch {
          return null
        }
      }}
    />
  )
}
